import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import { CONTACT_EMAIL } from "../data/constants";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const Container = styled.div`
	max-width: 900px;
	margin: 0 auto;
	padding: 3rem 2rem 4rem 2rem;

	@media (max-width: 768px) {
		padding: 2rem 1rem 3rem 1rem;
	}
`;

const Header = styled.div`
	margin-bottom: 3rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.5rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const LastUpdated = styled.p<{ theme: Theme }>`
	font-size: 0.95rem;
	color: ${props => props.theme.colors.text.tertiary};
	margin-top: 1rem;
`;

const Content = styled.div`
	animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const Section = styled.section`
	margin-bottom: 3rem;
`;

const SectionTitle = styled.h2<{ theme: Theme }>`
	font-size: 1.5rem;
	font-weight: 700;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 1rem;
	padding-bottom: 0.5rem;

	@media (max-width: 768px) {
		font-size: 1.25rem;
	}
`;

const SectionContent = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;
	white-space: pre-line;
`;

const ContactBox = styled.div<{ theme: Theme }>`
	padding: 2rem;
	border-radius: 12px;
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	text-align: center;
`;

const ContactDescription = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 1.5rem;
	line-height: 1.8;
`;

const EmailLink = styled.a<{ theme: Theme }>`
	display: inline-block;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 1.125rem;
	text-decoration: none;
	padding: 1rem 2rem;
	border-radius: 8px;
	background: ${props => props.theme.mode === 'light'
		? 'rgba(59, 130, 246, 0.1)'
		: 'rgba(59, 130, 246, 0.2)'};
	transition: all 0.3s ease;
	margin-bottom: 1rem;

	&:hover {
		background: ${props => props.theme.mode === 'light'
			? 'rgba(59, 130, 246, 0.2)'
			: 'rgba(59, 130, 246, 0.3)'};
		transform: translateY(-2px);
	}

	@media (max-width: 768px) {
		font-size: 1rem;
		padding: 0.875rem 1.5rem;
	}
`;

const FooterWrapper = styled.div`
	margin-top: 6rem;
`;

const Disclaimer = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const lastUpdatedDate = "2025年1月10日";

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${t.disclaimer.title} | ${userInfo.main.title}`}</title>
				<meta name="description" content={t.disclaimer.title} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar />
				<Container>
					<Header>
						<Title theme={theme}>{t.disclaimer.title}</Title>
						<LastUpdated theme={theme}>
							{t.disclaimer.lastUpdated}: {lastUpdatedDate}
						</LastUpdated>
					</Header>

					<Content>
						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.siteInfo.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.siteInfo.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.appUsage.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.appUsage.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.links.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.links.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.technicalIssues.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.technicalIssues.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.thirdPartyServices.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.thirdPartyServices.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.copyright.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.copyright.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.limitation.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.limitation.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.changes.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.disclaimer.sections.changes.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.disclaimer.sections.contact.title}
							</SectionTitle>
							<ContactBox theme={theme}>
								<ContactDescription theme={theme}>
									{t.disclaimer.sections.contact.content}
								</ContactDescription>
								<EmailLink
									href={`mailto:${CONTACT_EMAIL}`}
									theme={theme}
								>
									{CONTACT_EMAIL}
								</EmailLink>
							</ContactBox>
						</Section>
					</Content>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</Container>
			</PageContainer>
		</React.Fragment>
	);
};

export default Disclaimer;
